import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  useLocation } from 'react-router-dom';
import { Badge,
  Box,
  Button,
  Popover,
  Drawer,
  Tooltip,
  useTheme
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { get } from '@/utils/apiClient';
import { useAuthReady } from '@/hooks/useAuthReady';
import { todayLocalDate } from '@/utils/dateUtils';
import TodayBriefingPanel from './TodayBriefingPanel';
import StreakBadge from '@/components/gamification/StreakBadge';
import { logger } from "../../../utils/logger";
import { useIsMobile } from '../../../hooks/useMediaQuery';

// Routes where the briefing pill should NOT appear (HIPAA compliance)
// These are login, demo, and public pages where PHI should not be visible
const EXCLUDED_ROUTES = [
  '/login',
  '/demo',
  '/demo-login',
  '/signup',
  '/register',
  '/portal/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/book',
  '/testing',
  '/oauth-callback',
];

// Check if current route should show the briefing pill
function shouldShowOnRoute(pathname) {
  return !EXCLUDED_ROUTES.some(route => pathname.startsWith(route));
}

// Check if user is a client (briefing should NOT show for clients)
// Matches the logic in app/router/helpers.js for consistency
// Client types include: client, parent_of_client, part_of_couple
function isClientUser(user) {
  if (!user) return false;
  
  // For demo users, check character_type for client-like roles
  if (user.demo_mode) {
    const clientTypes = ['client', 'parent_of_client', 'part_of_couple'];
    return clientTypes.includes(user.character_type);
  }
  
  // For non-demo users, check roles array or single role field
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.includes('client');
}

function isMedicalAssistant(user) {
  if (!user) return false;
  if (user.demo_mode) {
    return user.character_type === 'medical_assistant';
  }
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.includes('medical_assistant');
}

/**
 * Today's Briefing pill component for the header.
 * Shows a pill that expands to show daily briefing for therapists.
 * Only visible for therapist/provider roles on appropriate pages.
 * 
 * HIPAA Compliance: This component is hidden on login/demo/public pages
 * to prevent PHI exposure before proper authentication.
 */
function TodayBriefingPill() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { ready, user } = useAuthReady();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [briefingData, setBriefingData] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedPreSession, setExpandedPreSession] = useState(false);
  const [preSessionData, setPreSessionData] = useState(null);
  
  const open = Boolean(anchorEl);

  // HIPAA Compliance: Check if we should show the briefing pill
  // 1. Must be on an allowed route (not login/demo/public pages)
  // 2. Must not be a client user (briefing is for staff only)
  const showOnThisRoute = shouldShowOnRoute(location.pathname);
  const isClient = isClientUser(user);
  const shouldShowBriefing = user && showOnThisRoute && !isClient;

  const isMA = isMedicalAssistant(user);

  const fetchBriefing = useCallback(async (isPolling = false) => {
    if (!ready || !user || !shouldShowBriefing) return;
    
    try {
      setLoading(!isPolling);
      const endpoint = isMA
        ? '/api/therapist_dashboard/briefing/ma'
        : '/api/therapist_dashboard/briefing';
      const response = await get(endpoint);
      if (response.ok) {
        const data = await response.json();
        
        // Check for new updates
        if (briefingData && isPolling) {
          const hasNewInfo = 
            data.alerts?.has_new_secure_messages ||
            data.completed_sessions !== briefingData.completed_sessions ||
            data.progress?.notes_signed_today !== briefingData.progress?.notes_signed_today;
          
          // Only show update indicator at :50 past the hour for non-message updates
          const now = todayLocalDate();
          const minutes = now.getMinutes();
          const isNearHourEnd = minutes >= 50 && minutes < 55;
          
          if (data.alerts?.has_new_secure_messages) {
            setHasUpdate(true);
            setHasNewMessages(true);
          } else if (hasNewInfo && isNearHourEnd) {
            setHasUpdate(true);
          }
        }
        
        setBriefingData(data);
      }
    } catch (error) {
      logger.error('Error fetching briefing:', error);
    } finally {
      setLoading(false);
    }
  }, [ready, user, shouldShowBriefing, briefingData, isMA]);

  // Initial fetch - intentionally only depends on auth state, not fetchBriefing
  // to prevent re-fetching when briefingData changes
  useEffect(() => {
    if (ready && user && shouldShowBriefing) {
      fetchBriefing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, shouldShowBriefing]);

  // Polling every 5 minutes
  useEffect(() => {
    if (!ready || !user || !shouldShowBriefing) return;
    
    const interval = setInterval(() => {
      fetchBriefing(true);
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [ready, user, shouldShowBriefing, fetchBriefing]);

  // Re-fetch immediately when calendar mutations signal staleness
  useEffect(() => {
    const handler = () => fetchBriefing(true);
    window.addEventListener('briefing-stale', handler);
    return () => window.removeEventListener('briefing-stale', handler);
  }, [fetchBriefing]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Clear update indicators when opened
    setHasUpdate(false);
    setHasNewMessages(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setExpandedPreSession(false);
    setPreSessionData(null);
  };

  const handleViewPreSession = async (encounterId) => {
    try {
      const response = await get(`/api/calendar/appointments/${encounterId}/just-in-time-info`);
      if (response.ok) {
        const data = await response.json();
        setPreSessionData(data);
        setExpandedPreSession(true);
      }
    } catch (error) {
      logger.error('Error fetching pre-session summary:', error);
    }
  };

  const handleBackFromPreSession = () => {
    setExpandedPreSession(false);
    setPreSessionData(null);
  };

  // HIPAA Compliance: Don't render on excluded routes or for client users
  if (!shouldShowBriefing) {
    return null;
  }

  // Extract streak data from briefing for the compact header badge
  const streakData = briefingData?.documentation_streak || null;

  const pillContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Compact streak badge in header */}
      {streakData && streakData.current_streak > 0 && (
        <StreakBadge streak={streakData} compact />
      )}
      <Tooltip title="View your daily schedule, pending tasks, and session summaries" arrow>
        <Button
          onClick={handleClick}
          variant="text"
          size="small"
          startIcon={!isMobile ? <AssignmentIcon fontSize="small" /> : undefined}
          sx={{
            textTransform: 'none',
            color: hasNewMessages ? 'primary.main' : 'text.secondary',
            backgroundColor: hasNewMessages ? 'primary.50' : 'transparent',
            borderRadius: '16px',
            px: isMobile ? 1 : 1.5,
            py: 0.5,
            fontSize: '0.875rem',
            fontWeight: 500,
            minWidth: isMobile ? '36px' : 'auto',
            '&:hover': {
              backgroundColor: hasNewMessages ? 'primary.100' : 'action.hover',
            },
            transition: 'background-color 0.2s ease',
          }}
        >
          {isMobile ? (
            <AssignmentIcon fontSize="small" />
          ) : (
            "Today's Briefing"
          )}
          {hasUpdate && (
            <Badge
              variant="dot"
              color={hasNewMessages ? 'primary' : 'default'}
              sx={{ ml: 0.5 }}
            />
          )}
        </Button>
      </Tooltip>
    </Box>
  );

  const panelContent = (
    <TodayBriefingPanel
      data={briefingData}
      loading={loading}
      onClose={handleClose}
      onViewPreSession={handleViewPreSession}
      expandedPreSession={expandedPreSession}
      preSessionData={preSessionData}
      onBackFromPreSession={handleBackFromPreSession}
      isMobile={isMobile}
    />
  );

  return (
    <>
      {pillContent}
      
      {isMobile ? (
        <Drawer
          anchor="top"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
            }
          }}
        >
          {panelContent}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 380,
              maxHeight: 500,
              mt: 1,
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            }
          }}
        >
          {panelContent}
        </Popover>
      )}
    </>
  );
}

export default memo(TodayBriefingPill);
